import { createMemo, createSignal } from "solid-js";
import type { Coordinates, EntitySize } from "~/solidJs/shared/helpers/types/ISizeble";
import type { CreditsBadgePopoverModel } from ".";
import type { CostDetailItem, CreditsBadgePopoverState } from "./model";

// Глобальный сигнал для состояния CreditsBadgePopover
const [globalState, setGlobalState] = createSignal<CreditsBadgePopoverState>({
	isOpen: false,
	position: { left: 0, top: 0, height: 0, width: 0 },
	nodeContextData: null
});

// Глобальная функция для открытия CreditsBadgePopover
export const openCreditsBadgePopover = (
	position: Coordinates & EntitySize,
	costData: CreditsBadgePopoverModel.NodeContextData
) => {
	setGlobalState({
		isOpen: true,
		position,
		nodeContextData: costData
	});
};

// Глобальная функция для закрытия CreditsBadgePopover
export const closeCreditsBadgePopover = () => {
	setGlobalState({
		isOpen: false,
		position: { left: 0, top: 0, height: 0, width: 0 },
		nodeContextData: null
	});
};

const createCostDetailItems = (
	nodeContextData: CreditsBadgePopoverModel.NodeContextData | null
): CostDetailItem[] => {
	if (!nodeContextData) return [];

	const items: CostDetailItem[] = [];

	items.push({
		name: nodeContextData.node.name,
		price: nodeContextData.costData.details.base_price
	});

	for (const param of nodeContextData.costData.details.params) {
		if (param.cost <= 0) continue;
		items.push({
			name:
				nodeContextData.node.definition.params
					.find((p) => p.name === param.name)
					?.getName() || param.name,
			value: param.value,
			price: param.cost
		});
	}

	for (const input of nodeContextData.costData.details.inputs) {
		if (input.cost <= 0) continue;
		items.push({
			name:
				nodeContextData.node.definition.inputs
					.find((i) => i.name === input.name)
					?.getName() || input.name,
			value: input.value,
			price: input.cost
		});
	}

	return items;
};

export const useCreditsBadgePopover = () => {
	const costDetailItems = createMemo(() => createCostDetailItems(globalState().nodeContextData));

	return {
		isOpen: () => globalState().isOpen,
		setIsOpen: (value: boolean) => {
			if (!value) {
				closeCreditsBadgePopover();
			} else {
				const currentState = globalState();
				setGlobalState({
					...currentState,
					isOpen: true
				});
			}
		},
		position: () => globalState().position,
		costData: () => globalState().nodeContextData,
		costDetailItems
	};
};
